import { LoaderFunction } from 'react-router-dom';

// Graphql
import { client } from '../../../../gql/client';
import { FETCH_COMMUNICATIONS } from '../../../../gql/queries/communication';
import { GET_SELECTORS_DATA, GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_USERS } from '../../../../gql/queries/users';

// Types
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// Selectors
import { selectCommunicationOrders } from '../../../../gql/selectors/communications';
import { store } from '../../../../state/configureStore';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';

export const LoaderCommunicationDuplicates: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const dispatch = store.dispatch;
  if (userData?.user) {
    const cursorCommunication = getCursor(tableIds.DUPLICATE_COMMUNICATION)(
      store.getState()
    );

    const roleActive = userData.user.user.rolActive;

    // FIX A4: Si concessionId no está seteada, pre-setear si solo hay 1 concesión.
    let effectiveConcessionId = cursorCommunication.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableIds.DUPLICATE_COMMUNICATION, { ...cursorCommunication, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido.
    dispatch(loadingData(tableIds.DUPLICATE_COMMUNICATION, false));

    const readData = client.readQuery({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: cursorCommunication.take,
          cursor: cursorCommunication.cursor,
          orderBy: cursorCommunication.orderBy,
          sortField: cursorCommunication.sortField,
          roadId: cursorCommunication.roadId,
          concessionId: effectiveConcessionId,
          interlocutorGroupId: cursorCommunication.interlocutorGroupId,
          fromDate: cursorCommunication.fromDate,
          toDate: cursorCommunication.toDate,
          indexType: cursorCommunication.indexType,
          indexExpedition: cursorCommunication.indexExpedition,
          classification: cursorCommunication?.classification,
          states: cursorCommunication.states,
          userIds: cursorCommunication.userIds,
          teamIds: cursorCommunication.teamIds,
          pkInitKm: cursorCommunication?.pkInitKm,
          pkInitMeter: cursorCommunication?.pkInitMeter,
          pkEndKm: cursorCommunication?.pkEndKm,
          pkEndMeter: cursorCommunication?.pkEndMeter,
          description: cursorCommunication?.description,
          predefinedOrderId: cursorCommunication?.predefinedOrderId,
        },
      },
    });
    const totalCount = readData?.communications?.totalCount;
    const pageInfo = readData?.communications?.pageInfo;
    const isCacheEmpty = JSON.stringify(readData) === '{}';
    const ordersWithDataCommunication =
      readData &&
      !isCacheEmpty &&
      (await selectCommunicationOrders(
        readData?.communications?.communications
      ));

    const [filtersData, usersResult] = await Promise.all([
      client.query({
        query: GET_SELECTORS_DATA,
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const combinedData = {
      ordersWithDataCommunication,
      filtersData: {
        ...filtersData,
        data: {
          ...filtersData.data,
          users: usersResult.data?.users,
        },
      },
      totalCount,
      pageInfo,
      roleActive,
    };

    if (!readData || isCacheEmpty) {
      dispatch(loadingData(tableIds.DUPLICATE_COMMUNICATION, true));
      try {
      let data;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        const effectiveTake = firstPageTake(cursorCommunication.take, takeBase);
        data = await client.query({
          query: FETCH_COMMUNICATIONS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              orderBy: cursorCommunication.orderBy ?? 'desc',
              sortField: cursorCommunication.sortField,
              roadId: cursorCommunication.roadId,
              concessionId: effectiveConcessionId,
              interlocutorGroupId: cursorCommunication.interlocutorGroupId,
              fromDate: cursorCommunication.fromDate,
              toDate: cursorCommunication.toDate,
              indexType: cursorCommunication.indexType,
              indexExpedition: cursorCommunication.indexExpedition,
              classification: cursorCommunication?.classification,
              states: cursorCommunication.states,
              userIds: cursorCommunication.userIds,
              teamIds: cursorCommunication.teamIds,
              pkInitKm: cursorCommunication?.pkInitKm,
              pkInitMeter: cursorCommunication?.pkInitMeter,
              pkEndKm: cursorCommunication?.pkEndKm,
              pkEndMeter: cursorCommunication?.pkEndMeter,
              description: cursorCommunication?.description,
              predefinedOrderId: cursorCommunication?.predefinedOrderId,
            },
          },
        });

        // OMIT sortField/orderBy en setCursorData: el merge de cursorTables
        // preserva los del cursor activo (mismo patrón que CommunicationsPage/loader.ts)
        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          roadId: cursorCommunication.roadId,
          concessionId: effectiveConcessionId,
          interlocutorGroupId: cursorCommunication.interlocutorGroupId,
          fromDate: cursorCommunication.fromDate,
          toDate: cursorCommunication.toDate,
          indexType: cursorCommunication.indexType,
          indexExpedition: cursorCommunication.indexExpedition,
          classification: cursorCommunication?.classification,
          states: cursorCommunication.states,
          userIds: cursorCommunication.userIds,
          teamIds: cursorCommunication.teamIds,
          pkInitKm: cursorCommunication?.pkInitKm,
          pkInitMeter: cursorCommunication?.pkInitMeter,
          pkEndKm: cursorCommunication?.pkEndKm,
          pkEndMeter: cursorCommunication?.pkEndMeter,
          description: cursorCommunication?.description,
          predefinedOrderId: cursorCommunication?.predefinedOrderId,
        };

        dispatch(setCursor(tableIds.DUPLICATE_COMMUNICATION, setCursorData));
      } else {
        data = await client.query({
          query: FETCH_COMMUNICATIONS,
          variables: {
            input: {
              take: cursorCommunication.take,
              cursor: cursorCommunication.cursor,
              orderBy: cursorCommunication.orderBy,
              sortField: cursorCommunication.sortField,
              roadId: cursorCommunication.roadId,
              concessionId: effectiveConcessionId,
              interlocutorGroupId: cursorCommunication.interlocutorGroupId,
              fromDate: cursorCommunication.fromDate,
              toDate: cursorCommunication.toDate,
              indexType: cursorCommunication.indexType,
              indexExpedition: cursorCommunication.indexExpedition,
              classification: cursorCommunication?.classification,
              states: cursorCommunication.states,
              userIds: cursorCommunication.userIds,
              teamIds: cursorCommunication.teamIds,
              pkInitKm: cursorCommunication?.pkInitKm,
              pkInitMeter: cursorCommunication?.pkInitMeter,
              pkEndKm: cursorCommunication?.pkEndKm,
              pkEndMeter: cursorCommunication?.pkEndMeter,
              description: cursorCommunication?.description,
              predefinedOrderId: cursorCommunication?.predefinedOrderId,
            },
          },
        });
      }

      const totalCount = data.data.communications.totalCount;
      const pageInfo = data.data.communications.pageInfo;

      const ordersWithDataCommunication = await selectCommunicationOrders(
        data?.data?.communications?.communications
      );
      const loading = data.loading;
      const [filtersData, usersResult] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_USERS,
          fetchPolicy: 'cache-first',
        }),
      ]);

      const combinedData = {
        ordersWithDataCommunication,
        filtersData: {
          ...filtersData,
          data: {
            ...filtersData.data,
            users: usersResult.data?.users,
          },
        },
        totalCount,
        pageInfo,
        loading,
        roleActive,
      };

      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
        dispatch(loadingData(tableIds.DUPLICATE_COMMUNICATION, false));
      }
    }

    return combinedData;
  }
  return {};
};

  return { data: load() };
};
